import { Box, Card, CardContent, Typography, Button, Container, Paper, List, ListItem, ListItemIcon, ListItemText, Alert } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScienceIcon from '@mui/icons-material/Science';
import TimerIcon from '@mui/icons-material/Timer';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const ControlGroupWelcome = ({ setContent }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleProceedToSurvey = () => {
        // Mark that the user has seen the welcome page
        sessionStorage.setItem('controlGroupWelcomeSeen', 'true');

        // Set the content to survey introduction before navigating
        if (setContent) {
            setContent('surveyIntroduction');
        }
        navigate('/dashboard');
    };

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <Card elevation={3} sx={{ borderRadius: 2, overflow: 'visible' }}>
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
                        color: '#1976D2',
                        p: 3,
                        textAlign: 'center',
                        borderBottom: '3px solid #1976D2',
                    }}
                >
                    <ScienceIcon sx={{ fontSize: 60, mb: 2, color: '#1976D2' }} />
                    <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
                        {t('controlGroup.welcome.title', 'Welcome to the UFlow Study!')}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9, color: '#1565C0' }}>
                        {t('controlGroup.welcome.subtitle', 'Thank you for participating in our research study')}
                    </Typography>
                </Box>

                <CardContent sx={{ p: 4 }}>
                    <Alert severity="info" sx={{ mb: 3 }}>
                        <Typography>
                            {t('controlGroup.welcome.assignmentMessage',
                                'Your participation involves completing an initial survey and returning after 2 weeks to view your personalized results and recommendations.'
                            )}
                        </Typography>
                    </Alert>

                    <Paper elevation={0} sx={{ p: 3, bgcolor: '#F5F9FF', mb: 3, border: '1px solid #E3F2FD' }}>
                        <Typography variant="h6" gutterBottom sx={{ color: '#1976D2' }}>
                            <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#1976D2' }} />
                            {t('controlGroup.welcome.whatHappensNext', 'What Happens Next?')}
                        </Typography>

                        <List>
                            <ListItem>
                                <ListItemIcon>
                                    <CheckCircleOutlineIcon sx={{ color: '#42A5F5' }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={t('controlGroup.welcome.step1', 'Complete the Initial Survey')}
                                    secondary={t('controlGroup.welcome.step1Desc',
                                        'Fill out a comprehensive survey about your daily habits and sleep patterns. This will take approximately 15-20 minutes.'
                                    )}
                                />
                            </ListItem>

                            <ListItem>
                                <ListItemIcon>
                                    <TimerIcon sx={{ color: '#42A5F5' }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={t('controlGroup.welcome.step2', 'Wait for 2 Weeks')}
                                    secondary={t('controlGroup.welcome.step2Desc',
                                        'After completing the survey, you will need to wait 2 weeks before viewing your chronotype results. This is an important part of the study design.'
                                    )}
                                />
                            </ListItem>

                            <ListItem>
                                <ListItemIcon>
                                    <CheckCircleOutlineIcon sx={{ color: '#42A5F5' }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={t('controlGroup.welcome.step3', 'View Your Results')}
                                    secondary={t('controlGroup.welcome.step3Desc',
                                        'After 2 weeks, you will be able to access your personalized chronotype analysis and recommendations.'
                                    )}
                                />
                            </ListItem>
                        </List>
                    </Paper>

                    {/* Spacer before button */}
                    <Box sx={{ height: '40px' }} />

                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleProceedToSurvey}
                            sx={{
                                px: 6,
                                py: 1.5,
                                background: 'linear-gradient(135deg, #42A5F5 0%, #1976D2 100%)',
                                color: 'white',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #2196F3 0%, #1565C0 100%)',
                                }
                            }}
                        >
                            {t('controlGroup.welcome.proceedButton', 'Proceed to Initial Survey')}
                        </Button>
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 3 }}>
                        {t('controlGroup.welcome.studyDuration',
                            'Your participation in this study will last approximately 2 weeks from today.'
                        )}
                    </Typography>
                </CardContent>
            </Card>
        </Container>
    );
};

export default ControlGroupWelcome;